import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Science as ScienceIcon, Visibility as LogsIcon } from '@mui/icons-material';
import * as clinicLabIntegrationsService from '@services/clinicLabIntegrationsService';
import type { ClinicLabIntegration } from '@services/clinicLabIntegrationsService';
import { useAuth } from '@contexts/AuthContext';

const ClinicLabIntegrations: React.FC = () => {
  const { user } = useAuth();
  const clinicId = (user as { clinicId?: string })?.clinicId;
  const [list, setList] = useState<ClinicLabIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [labEmailText, setLabEmailText] = useState('');
  const [saving, setSaving] = useState(false);
  const [createdIntegration, setCreatedIntegration] = useState<ClinicLabIntegration | null>(null);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [logsIntegrationId, setLogsIntegrationId] = useState<string | null>(null);
  const [logs, setLogs] = useState<unknown[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const loadList = async () => {
    if (!clinicId) return;
    setLoading(true);
    setError('');
    try {
      const data = await clinicLabIntegrationsService.listClinicLabIntegrations(clinicId);
      setList(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Eroare la încărcare');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, [clinicId]);

  const handleOpenDialog = () => {
    setLabEmailText('');
    setCreatedIntegration(null);
    setError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (createdIntegration) loadList();
  };

  const handleSaveIntegration = async () => {
    const allowed = labEmailText
      .split(/[\n,;]/)
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
    if (allowed.length === 0) {
      setError('Introdu adresa de email a laboratorului.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const created = await clinicLabIntegrationsService.createClinicLabIntegration(
        { allowed_sender_emails: allowed, active: true },
        clinicId
      );
      setCreatedIntegration(created);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const openLogs = async (id: string) => {
    setLogsIntegrationId(id);
    setLogsDialogOpen(true);
    setLogsLoading(true);
    try {
      const data = await clinicLabIntegrationsService.getClinicLabIntegrationLogs(id, 50, 0);
      setLogs(data);
    } catch (_) {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScienceIcon /> Integrări laborator
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">Configurări active</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
              Adaugă integrare
            </Button>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Adresă inbound (pentru laborator)</TableCell>
                    <TableCell>Email laborator</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Niciun laborator configurat. Adaugă o integrare (doar adresa de email a laboratorului).
                      </TableCell>
                    </TableRow>
                  ) : (
                    list.map((i) => (
                      <TableRow key={i.id}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{i.inbound_email_address || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>
                          {(i.allowed_sender_emails && i.allowed_sender_emails.length > 0)
                            ? i.allowed_sender_emails.length === 1
                              ? i.allowed_sender_emails[0]
                              : `${i.allowed_sender_emails[0]} +${i.allowed_sender_emails.length - 1}`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip label={i.active ? 'Activ' : 'Inactiv'} color={i.active ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openLogs(i.id)} aria-label="Loguri">
                            <LogsIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Adaugă integrare laborator</DialogTitle>
        <DialogContent>
          {createdIntegration ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Integrare creată. Trimite laboratorului adresa de mai jos; de acolo vor fi acceptate doar email-urile introduse de tine.
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">Adresa inbound (o trimiți laboratorului):</Typography>
                <Typography component="code" sx={{ display: 'block', mt: 0.5, p: 1, bgcolor: 'grey.100', borderRadius: 1, wordBreak: 'break-all' }}>
                  {createdIntegration.inbound_email_address}
                </Typography>
              </Box>
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {error && (
                <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                Introdu adresa de email de la care laboratorul trimite rezultatele (ex: rezultate@laborator.ro). După salvare vei primi o adresă inbound pe care o trimiți laboratorului.
              </Typography>
              <TextField
                label="Adresa de email a laboratorului"
                placeholder="rezultate@laborator.ro"
                value={labEmailText}
                onChange={(e) => setLabEmailText(e.target.value)}
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                helperText="Mai multe adrese: câte una per linie sau separate prin virgulă"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{createdIntegration ? 'Închide' : 'Anulare'}</Button>
          {!createdIntegration && (
            <Button variant="contained" onClick={handleSaveIntegration} disabled={saving}>
              {saving ? 'Se salvează…' : 'Salvează'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Loguri integrare</DialogTitle>
        <DialogContent>
          {logsLoading ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tip</TableCell>
                    <TableCell>Payload</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(logs as { event_type?: string; payload?: unknown }[]).map((row: any, idx: number) => (
                    <TableRow key={row.id || idx}>
                      <TableCell>{row.event_type}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {typeof row.payload === 'object' ? JSON.stringify(row.payload).slice(0, 120) : String(row.payload)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialogOpen(false)}>Închide</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicLabIntegrations;
